import { consoleRoutes, type DashboardRange } from "@sagot/constants";
import type { BadgeTone, IconName, StatDelta } from "@sagot/ui";
import type { SetupStep } from "../shared/api-types";

/** Greeting by hour of day in the workspace timezone. */
export function greetingKey(hour: number): "morning" | "afternoon" | "evening" {
  if (hour < 12) return "morning";
  if (hour < 18) return "afternoon";
  return "evening";
}

export function hourIn(timeZone: string, at: Date = new Date()): number {
  const hour = new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", hourCycle: "h23" }).format(at);
  return Number(hour) % 24;
}

export function firstName(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  const skip = /^(ma\.|mr\.|ms\.|mrs\.|dr\.|fr\.|engr\.|atty\.)$/i;
  return parts.find((part) => !skip.test(part)) ?? parts[0] ?? fullName;
}

/**
 * Delta badge for a KPI. `higherIsBetter` decides the tone (e.g. fewer handoffs is good news);
 * returns undefined when there is no earlier period to compare.
 */
export function deltaOf(value: number | null | undefined, label: string, higherIsBetter = true): StatDelta | undefined {
  if (value === null || value === undefined || !Number.isFinite(value)) return undefined;
  const direction = value >= 0 ? "up" : "down";
  return { direction, label, positive: higherIsBetter ? value >= 0 : value <= 0 };
}

/** `18.2` → `"+18%"`, `-9` → `"−9%"` (true minus sign, as in the mockup). */
export function signedPercent(value: number): string {
  const rounded = Math.round(value);
  return `${rounded > 0 ? "+" : rounded < 0 ? "−" : ""}${String(Math.abs(rounded))}%`;
}

export function signedPoints(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return `${rounded > 0 ? "+" : rounded < 0 ? "−" : ""}${String(Math.abs(rounded))}`;
}

/** Fractions (0–1) and percentages (0–100) both arrive from analytics; normalise to 0–100. */
export function toPercent(value: number): number {
  return value <= 1 ? value * 100 : value;
}

export function seconds(ms: number): string {
  return (ms / 1000).toFixed(1);
}

/** Up to four evenly spaced labels under the daily chart (first and last always included). */
export function axisPicks<T>(items: readonly T[], count = 4): T[] {
  if (items.length <= count) return [...items];
  const step = (items.length - 1) / (count - 1);
  return Array.from({ length: count }, (_, i) => items[Math.round(i * step)]).filter((item): item is T => item !== undefined);
}

export const CHANNEL_ICONS: Readonly<Record<string, IconName>> = {
  widget: "globe",
  webapp: "app-window",
  mobile: "smartphone",
  messenger: "message-circle",
  email: "mail",
  api: "code",
  playground: "flask",
};

export const STATUS_TONES: Readonly<Record<string, { tone: BadgeTone; icon: IconName }>> = {
  resolved: { tone: "success", icon: "check-circle" },
  needs_staff: { tone: "danger", icon: "headset" },
  lead_captured: { tone: "brand", icon: "user-check" },
  open: { tone: "neutral", icon: "circle" },
  closed: { tone: "neutral", icon: "check" },
};

/** Where an unfinished setup step takes the user. */
export function checklistHref(step: SetupStep, ws: string, agentId: string | null): string {
  if (!agentId) return step === "invite_team" ? consoleRoutes.team(ws) : consoleRoutes.agents(ws);
  const routes: Readonly<Record<SetupStep, string>> = {
    create_agent: consoleRoutes.agents(ws),
    add_knowledge: consoleRoutes.knowledge(ws, agentId),
    test_agent: consoleRoutes.playground(ws, agentId),
    install_channel: consoleRoutes.widgetStudio(ws, agentId),
    invite_team: consoleRoutes.team(ws),
    go_live: consoleRoutes.channels(ws, agentId),
  };
  return routes[step];
}

export function isDashboardRange(value: string | null): value is DashboardRange {
  return value === "7d" || value === "30d" || value === "90d";
}
